import React from 'react';
import { Link } from 'react-router-dom';
import { Database, Shield, ExternalLink, Sparkles, Linkedin } from 'lucide-react';
import BrandLogo from './BrandLogo';

const Footer = () => {
  return (
    <footer className="relative overflow-hidden border-t border-violet-100 bg-white text-slate-900" data-testid="footer">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(181,126,220,0.12),_transparent_28%),linear-gradient(180deg,_rgba(255,255,255,1)_0%,_rgba(248,245,255,0.9)_100%)]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-300/80 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-4 gap-8 mb-10">
          <div>
            <BrandLogo textClassName="text-slate-900 group-hover:text-violet-700" imageClassName="opacity-95" />
            <p className="mt-4 text-slate-600 text-sm leading-relaxed max-w-sm">
              Public health data, broken down into clear explanations, practical context, and transparent sourcing.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700">
              <Sparkles className="w-3.5 h-3.5" /> Built for curious learners, patients, students, and communities
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-violet-700">Explore</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/chronic-disease" className="hover:text-violet-700 transition-colors">Chronic Disease</Link></li>
              <li><Link to="/autoimmune" className="hover:text-violet-700 transition-colors">Autoimmune Diseases</Link></li>
              <li><Link to="/vaccines" className="hover:text-violet-700 transition-colors">Vaccines & Public Health</Link></li>
              <li><Link to="/risk" className="hover:text-violet-700 transition-colors">See Yourself in the Data</Link></li>
              <li><Link to="/learn" className="hover:text-violet-700 transition-colors">Learn Public Health</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-violet-700 flex items-center gap-2">
              <Database className="w-4 h-4 text-violet-500" /> Data Transparency
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li>
                <a href="https://data.cdc.gov" target="_blank" rel="noopener noreferrer" className="hover:text-violet-700 flex items-center gap-1">
                  CDC PLACES 2025 <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a href="https://data.cdc.gov" target="_blank" rel="noopener noreferrer" className="hover:text-violet-700 flex items-center gap-1">
                  CDC BRFSS <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>CDC Vaccination Coverage</li>
              <li>Massachusetts DPH</li>
              <li><a href="https://data.cdc.gov" target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:text-violet-800 transition-colors flex items-center gap-1">CDC Open Data <ExternalLink className="w-3 h-3" /></a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-violet-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-violet-500" /> Mission & Contact
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Public health, explained in plain English
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Transparent sourcing and update notes
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Educational only, not medical advice
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/mariamongiardo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-2 text-violet-700 hover:bg-violet-100 hover:text-violet-800 transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> Connect with Maria Mongiardo
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-violet-100 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm text-slate-500">
          <p>
            &copy; {new Date().getFullYear()} EpiSplain · Public Health, Explained Simply · For educational purposes only
          </p>
          <p className="text-xs text-slate-400">
            Designed to make epidemiology and health data easier to understand for everyone.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
